import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import type { BadgeNotificationData } from '../types/gamification.types';
import { RARITY_COLORS } from '../constants/badges';
import { BadgeIcon } from './BadgeIcon';

interface BadgeNotificationProps {
  data: BadgeNotificationData;
  onDismiss: () => void;
}

const AUTO_CLOSE_MS = 6000;

export function BadgeNotification({ data, onDismiss }: BadgeNotificationProps) {
  const [progress, setProgress] = useState(100);
  const colors = RARITY_COLORS[data.badge.rarity];

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / AUTO_CLOSE_MS) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onDismiss();
      }
    }, 50);
    return () => clearInterval(interval);
  }, [onDismiss]);

  return (
    <motion.div
      className="fixed inset-0 z-notification flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onDismiss}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Particles */}
      <Particles color={colors.particle} />

      {/* Orbital Rings */}
      <OrbitalRings color={colors.primary} />

      {/* Card */}
      <motion.div
        className="relative z-10 w-[340px] max-w-[90vw]"
        initial={{ scale: 0.8, y: 50, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.8, y: 50, opacity: 0 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-white/10">
          {/* Glow effect */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              background: `radial-gradient(circle at 50% 0%, ${colors.primary}, transparent 70%)`,
            }}
          />

          <div className="relative p-8 text-center">
            {/* Hex Frame + Icon */}
            <div className="relative mx-auto w-24 h-24 mb-6">
              <HexFrame color={colors.primary} glow={colors.glow} />
              <div className="absolute inset-0 flex items-center justify-center">
                <BadgeIcon
                  iconName={data.badge.icon}
                  className="w-10 h-10"
                  style={{ color: colors.primary }}
                />
              </div>
            </div>

            {/* Badge unlocked label */}
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color: colors.secondary }}>
              Badge Débloqué
            </p>

            {/* Badge name */}
            <h3 className="text-2xl font-bold text-white mb-2">{data.badge.name}</h3>

            {/* Description */}
            <p className="text-sm text-slate-400 leading-relaxed">{data.badge.description}</p>

            {/* Rarity tag */}
            <div className="mt-4">
              <span
                className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={{ backgroundColor: `${colors.primary}20`, color: colors.primary }}
              >
                {data.badge.rarity === 'common' ? 'Commun' : data.badge.rarity === 'rare' ? 'Rare' : 'Épique'}
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-slate-800">
            <motion.div
              className="h-full"
              style={{ backgroundColor: colors.primary, width: `${progress}%` }}
              transition={{ duration: 0.05 }}
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Particles({ color }: { color: string }) {
  // Générées une seule fois au montage pour rester pures au re-render
  const [particles] = useState(() =>
    Array.from({ length: 40 }, (_, i) => ({
      angle: (i / 40) * 360,
      distance: 80 + Math.random() * 200,
      size: 2 + Math.random() * 4,
      duration: 1.5 + Math.random() * 2,
      delay: Math.random() * 0.5,
    }))
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(({ angle, distance, size, duration, delay }, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: size,
            height: size,
            backgroundColor: color,
            left: '50%',
            top: '50%',
          }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{
            x: Math.cos((angle * Math.PI) / 180) * distance,
            y: Math.sin((angle * Math.PI) / 180) * distance,
            opacity: 0,
            scale: 0,
          }}
          transition={{ duration, delay, ease: 'easeOut' }}
        />
      ))}
    </div>
  );
}

function OrbitalRings({ color }: { color: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {[120, 160].map((size, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border"
          style={{
            width: size,
            height: size,
            borderColor: `${color}30`,
          }}
          animate={{ rotate: i === 0 ? 360 : -360 }}
          transition={{ duration: 8 + i * 4, repeat: Infinity, ease: 'linear' }}
        />
      ))}
    </div>
  );
}

function HexFrame({ color, glow }: { color: string; glow: string }) {
  return (
    <motion.div
      className="absolute inset-0"
      animate={{ boxShadow: [`0 0 20px ${glow}`, `0 0 40px ${glow}`, `0 0 20px ${glow}`] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <polygon
          points="50,2 93,25 93,75 50,98 7,75 7,25"
          fill="none"
          stroke={color}
          strokeWidth="2"
          opacity="0.6"
        />
        <polygon
          points="50,10 85,29 85,71 50,90 15,71 15,29"
          fill={`${color}10`}
          stroke={color}
          strokeWidth="1"
          opacity="0.3"
        />
      </svg>
    </motion.div>
  );
}
